import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import compoundComponentsPng from "../../static/blog_solid_principle_main_image.jpeg";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ImageListItem from '@mui/material/ImageListItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { CodeBlock, dracula } from 'react-code-blocks';


function PrincipleWrapper({data}) {
  if(!data) return null;
    return (
      <TableContainer component={Paper}>
  <Table
    sx={{
      fontFamily: `'Fira Code', 'JetBrains Mono', 'Consolas', monospace`,
      tableLayout: 'fixed',
      '@media (max-width: 900px)': {
        '& td, & th, & tr': {
          display: 'block',
          width: '100%',
        },
      },
    }}
    aria-label="simple table"
  >
    <TableHead>
      <TableRow>
        <TableCell align="center"><strong>{data?.titleOne}</strong></TableCell>
        <TableCell align="center"><strong>{data?.TitleTwo}</strong></TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell align="left" sx={{ width: 6000}} >
          <CodeBlock
            text={data.snippetOne}
            language={'javascript'}
            showLineNumbers
            theme={dracula}
          />
        </TableCell>
        <TableCell align="left">
          <CodeBlock
            text={data.snippetTwo}
            language={'javascript'}
            showLineNumbers
            theme={dracula}
          />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</TableContainer>
    );
  }

const codeWithoutSingleResponsibilityPrinciple = `
import { Sequelize } from 'sequelize';

const dbConfig = config[env];

class Database {
  private connection: Sequelize | null = null;

  private async connect (): Promise<void> {
    try {
      //Responsibility One: Connect to Database
      this.connection = new Sequelize(
        dbConfig.database,
        dbConfig.username,
        dbConfig.password, {
          host: dbconfig.host,
          dialect: dbConfig.dialect,
        }
      );
      await this.connection.authenticate();
      console.log('Connected to MySQL database successfully');

      //Responsibility Two: Initialize sequelize models
      fs.readdirSync(__dirname)
        .filter((file) => {
          file.indexOf('.') !== 0 &&
          file !== basename &&
          file.slice(-3) === '.js' &&
          file.indexOf('.test.js') === -1
        })
        .forEach((file) => {
          const model = require(path.join(__dirname, file)).default(this.connection, DataTypes);
          this.connection!.models[model.name] = model;
        });
      
      Object.values(this.connection!.models).forEach((model) => {
        if (typeof model.associate === 'function') {
          model.associate(this.connection!.models);
        }
      });
    } catch (err) {
      console.error('Error connecting to MySQL: ', err);
      throw err; 
    }
  }
}








`;

const codeSingleResponsibilityPrinciple = `
import { Sequelize } from 'sequelize';

const dbConfig = config[env];

class Database {
  private connection: Sequelize | null = null;

  private async connect (): Promise<void> {
    try {
      //Single Responsibility Only: Connect to Database
      this.connection = new Sequelize(
        dbConfig.database,
        dbConfig.username,
        dbConfig.password, {
          host: dbconfig.host,
          dialect: dbConfig.dialect,
        }
      );
      await this.connection.authenticate();
      console.log('Connected to MySQL database successfully');
      //Moves Responsibility Two: Initialize models to dedicated class
      const modelInitializer = new ModelInitializer(this.connection);
      modelInitializer.initialiseModels();
    } catch (err) {
      console.error('Error connecting to MySQL: ', err);
      throw err; 
    }
  }
}

class ModelInitializer {
  constructor(private connection: Sequelize) {}

  public initialiseModels(): Promise<void> {
    fs.readdirSync(__dirname)
        .filter((file) => {
          file.indexOf('.') !== 0 &&
          file !== basename &&
          file.slice(-3) === '.js' &&
          file.indexOf('.test.js') === -1
        })
        .forEach((file) => {
          const model = require(path.join(__dirname, file)).default(this.connection, DataTypes);
          this.connection!.models[model.name] = model;
        });
      
      Object.values(this.connection!.models).forEach((model) => {
        if (typeof model.associate === 'function') {
          model.associate(this.connection!.models);
        }
      });
  }
}
`;


const codeWithoutOpenClosedPrinciple = `
import express from "express";
import Database, { Op, QueryTypes } from "../db/models";

const attendanceRouter = express.Router();

attendanceRouter.post('/saveAttendance', async function (req: any, res: any) {
  let db = await Database.getInstance();
  const t = await db.getConnection().transaction();
    try {
        let { attendance_records: attendanceRecords } = req.body;
        const { AttendanceRecord } = db.getConnection().models;
       
        const attendanceValues = attendanceRecords
        .map((record: any) => ({
            id: record.record_id ?? undefined,
            record_date: record.timestamp,
            daily_wage_amount: record.daily_wage_amount,
            is_valid: record.is_valid ?? true,
            //Multiple Roles being handled and violates Open-Closed Principle
            labourer_role_id: record.labourer_role_id,
            contractor_role_id: record.contractor_role_id,
            /*
              New roles would need changes to this code snippet,
              which basically implices its not closed for modification

              For any extension lets say we want to add another usecase transporter_role_id,
              it would require modification of this code
            */
         
        }));
        if (attendanceValues.length) {
          await AttendanceRecord.bulkCreate(attendanceValues, {
            updateOnDuplicate: [
              'attendance_multiplier',
              'is_valid',
              'daily_wage_amount'
            ],
            validate: true,
            transaction: t,
          });
        }
        t.commit();
        res.json({
          message: "Attendance recorded",
        });
      } catch (err: any) {
        t.rollback();
        console.error("Error occured due to ", { err });
        res.status(500).json({
          message: "Error occured while saving labourers attendance"
        });
      }
});

export default attendanceRouter;

































`;

const codeWithOpenClosedPrinciple = `
import express from "express";
import Database, { Op, QueryTypes } from "../db/models";

const attendanceRouter = express.Router();

interface RoleHandler {
  canHandle(record: any): boolean;
  transform(record:any): Record<string, any>;
}

class LabourerRoleHandler implements RoleHandler {
  canHandle(record: any): boolean {
    return !!record.labourer_role_id;
  }

  transform(record: any): Record<string, any> {
    return {
      labourer_role_id: record.labourer_role_id,
    }
  }
}

class ContractorRoleHandler implements RoleHandler {
  canHandle(record: any): boolean {
    return !!record.contractor_role_id;
  }

  transform(record: any): Record<string, any> {
    return {
      contractor_role_id: record.contractor_role_id,
    }
  }
}

const roleHandlers: RoleHandler[] = [
  new LabourerRoleHandler(),
  new ContractorRoleHandler()
];

attendanceRouter.post('/saveAttendance', async function (req: any, res: any) {
  let db = await Database.getInstance();
  const t = await db.getConnection().transaction();
    try {
        let { attendance_records: attendanceRecords } = req.body;
        const { AttendanceRecord } = db.getConnection().models;
       
        const attendanceValues = attendanceRecords
        .map((record: any) => {
        //This code snippet is now closed for Modification
          const roleData = roleHandlers
          .find(handler => handler.canHandle(record))?.transform(record) || {};
        //But Open for extension as we can keep adding more roleHandler classes
        // We can just add another handler for transporter_role_id 
        // usecase and this function does not need any changes
          return {
              id: record.record_id ?? undefined,
              record_date: record.timestamp,
              daily_wage_amount: record.daily_wage_amount,
              is_valid: record.is_valid ?? true,
              ...roleData,
          }
        });
        if (attendanceValues.length) {
          await AttendanceRecord.bulkCreate(attendanceValues, {
            updateOnDuplicate: [
              'attendance_multiplier',
              'is_valid',
              'daily_wage_amount'
            ],
            validate: true,
            transaction: t,
          });
        }
        t.commit();
        res.json({
          message: "Attendance recorded",
        });
      } catch (err: any) {
        t.rollback();
        console.error("Error occured due to ", { err });
        res.status(500).json({
          message: "Error occured while saving labourers attendance"
        });
      }
});

export default attendanceRouter;

`;


function createSingleResponsibilityData() {
  return { 
    titleOne: 'Without Single Responsibility Principle',
    TitleTwo: 'With Single Responsibility Principle',
    snippetOne: codeWithoutSingleResponsibilityPrinciple,
    snippetTwo: codeSingleResponsibilityPrinciple,
  };
}

function createOpenClosedPrincipleData() {
  return { 
    titleOne: 'Without Open-Closed Principle',
    TitleTwo: 'With Open-Closed Principle',
    snippetOne: codeWithoutOpenClosedPrinciple,
    snippetTwo: codeWithOpenClosedPrinciple,
  };
}

const codeWithoutLiskovSubstitutionPrinciple = `
const finalResult = results.map(({ type, roles, id, resource_id }) => {
  /*
  we have code that behaved differently depending on the resource type (Labourer or Contractor)
  there’s no common superclass or abstraction
  we are manually differentiating between Labourer and Contractor,
  If a function written for T has to say “wait, are you actually an S?” 
  then S is not a true subtype of T.
  */
  const { roles: availableRoles, ...resourceBasicInfo } =
    type === "Labourer"
      ? labourersWithAttendance.find((l: any) => l.id === id)
      : contractorsWithAttendance.find((c: any) => c.id === id);

  const sortedRoles = availableRoles.sort((a: any, b: any) => b.is_primary - a.is_primary);
  
  const expandedRoles = sortedRoles.map((role: any) => ({
    ...role,
    ...(roles ?? []).find((attendanceRole: any) =>
      type === "Labourer"
        ? attendanceRole.labourer_role_id === role.labourer_role_id
        : attendanceRole.contractor_role_id === role.contractor_role_id
    ) ?? {},
  }));

  return {
    ...resourceBasicInfo,
    profile_image_url: getFormattedImageUrl(host, resourceBasicInfo.profile_image_url, timestamp),
    resource_id: bufferToUuid(resource_id),
    labourer_roles: sortedRoles
      .filter((activeRole: any) => activeRole.active_state === "active")
      .map((role: any) => ({ ...role, checked: role.is_primary ? true : false })),
    site_attendance: [{
      site_id: siteId,
      site_status: siteData?.site_status,
      labourer_roles: expandedRoles.filter(
        (validAttendanceRole: any) =>
          !(validAttendanceRole.active_state === "inactive" && validAttendanceRole.checked === false)
      ),
    }],
  };
});



























 












`;
const codeWithLiskovSubstitutionPrinciple = `
const finalResult = results.map(({ type, roles, id, resource_id }) => {
  const matchedData = type === "Labourer"
    ? labourersWithAttendance.find((l: any) => l.id === id)
    : contractorsWithAttendance.find((c: any) => c.id === id);
  const resource: Resource = type === "Labourer"
    ? new LabourerResource(matchedData)
    : new ContractorResource(matchedData);

  const availableRoles = resource.getAvailableRoles();
  const expandedRoles = resource.getExpandedRoles(roles);

  return {
    ...resource.getBasicInfo(),
    profile_image_url: getFormattedImageUrl(host, matchedData.profile_image_url, timestamp),
    resource_id: bufferToUuid(resource_id),
    labourer_roles: availableRoles
      .filter((role: any) => role.active_state === "active")
      .map((role: any) => ({ ...role, checked: role.is_primary })),
    site_attendance: [{
      site_id: siteId,
      site_status: siteData?.site_status,
      labourer_roles: expandedRoles.filter(
        (r: any) => !(r.active_state === "inactive" && r.checked === false)
      ),
    }]
  };
});


//to resolve we created a common interface:
interface Resource {
  id: string;
  type: 'Labourer' | 'Contractor';
  getAvailableRoles(): any[];
  getExpandedRoles(savedRoles: any[]): any[];
  getBasicInfo(): any;
}

class LabourerResource implements Resource {
  constructor(private raw: any) {}
  id = this.raw.id;
  type: 'Labourer' = 'Labourer';

  getAvailableRoles() {
    return this.raw.roles.sort((a: any, b: any) => b.is_primary - a.is_primary);
  }

  getExpandedRoles(savedRoles: any[]) {
    return this.getAvailableRoles().map((role: any) => ({
      ...role,
      ...(savedRoles.find((r: any) => r.labourer_role_id === role.labourer_role_id) ?? {})
    }));
  }

  getBasicInfo() {
    return { ...this.raw, type: this.type };
  }
}

class ContractorResource implements Resource {
  constructor(private raw: any) {}
  id = this.raw.id;
  type: 'Contractor' = 'Contractor';

  getAvailableRoles() {
    return this.raw.roles.sort((a: any, b: any) => b.is_primary - a.is_primary);
  }

  getExpandedRoles(savedRoles: any[]) {
    return this.getAvailableRoles().map((role: any) => ({
      ...role,
      ...(savedRoles.find((r: any) => r.contractor_role_id === role.contractor_role_id) ?? {})
    }));
  }

  getBasicInfo() {
    return { ...this.raw, type: this.type };
  }
}


`;

function createLiskovSubstitutionPrincipleData() {
  return { 
    titleOne: 'Without Liskov Substitution Principle',
    TitleTwo: 'With Liskov Substitution Principle',
    snippetOne: codeWithoutLiskovSubstitutionPrinciple,
    snippetTwo: codeWithLiskovSubstitutionPrinciple,
  };
}


const codeWithoutInterfaceSegregationPrinciple = `
interface Resource {
  getAvailableRoles(): any[];
  getExpandedRoles(savedRoles: any[]): any[];
  getBasicInfo(): any;
  getContractorLicenseNumber(): string;  // 👈 added
  getLabourerSafetyScore(): number;      // 👈 added
}

class LabourerResource implements Resource {
  getAvailableRoles() { ... }
  getExpandedRoles(savedRoles) { ... }
  getBasicInfo() { ... }
  getContractorLicenseNumber() { return ""; } // 😬 dummy implementation
  getLabourerSafetyScore() { return 85; }     // ✅ real
}

class ContractorResource implements Resource {
  getAvailableRoles() { ... }
  getExpandedRoles(savedRoles) { ... }
  getBasicInfo() { ... }
  getContractorLicenseNumber() { return "ABC123"; } // ✅ real
  getLabourerSafetyScore() { return 0; }            // 😬 dummy
}



`;
const codeWithInterfaceSegregationPrinciple = `
interface Resource {
  getAvailableRoles(): any[];
  getExpandedRoles(savedRoles: any[]): any[];
  getBasicInfo(): any;
}

interface ContractorExtra {
  getContractorLicenseNumber(): string;
}

interface LabourerExtra {
  getLabourerSafetyScore(): number;
}
class LabourerResource implements Resource, LabourerExtra {
  getAvailableRoles() { ... }
  getExpandedRoles(savedRoles) { ... }
  getBasicInfo() { ... }
  getLabourerSafetyScore() { return 85; }
}

class ContractorResource implements Resource, ContractorExtra {
  getAvailableRoles() { ... }
  getExpandedRoles(savedRoles) { ... }
  getBasicInfo() { ... }
  getContractorLicenseNumber() { return "ABC123"; }
}
`;

function createInterfaceSegregationPrincipleData() {
  return { 
    titleOne: 'Without Interface Segregation Principle',
    TitleTwo: 'With Interface Segregation Principle',
    snippetOne: codeWithoutInterfaceSegregationPrinciple,
    snippetTwo: codeWithInterfaceSegregationPrinciple,
  };
}

const codeWithoutDependencyInversionPrinciple = `
router.get('/cities', async function (req: any, res: any) {
    try {
        let db = await Database.getInstance();
        const { City } = db.getConnection().models;
        const cities = await City.findAll({
            where: {
                group_id: req.user.groupId
            }
        });
        res.json({ data: cities });

    } catch (err: any) {
        console.error("Error occured due to ", { err });
        res.status(500).json({
            message: "Error occured while fetching cities"
        });
    }
});
































`;
const codeWithDependencyInversionPrinciple = `
// services/CityService.ts
import Database from '../db/models';

export class CityService {
  async getCitiesByGroup(groupId: string) {
    const db = await Database.getInstance();
    const { City } = db.getConnection().models;
    return City.findAll({
      where: {
        group_id: groupId
      }
    });
  }
}

// controllers/CityController.ts
import { Request, Response } from 'express';
import { CityService } from '../services/CityService';

export class CityController {
  constructor(private cityService: CityService) {}

  getCities = async (req: Request, res: Response) => {
    try {
      const groupId = req.user.groupId;
      const cities = await this.cityService.getCitiesByGroup(groupId);
      res.json({ data: cities });
    } catch (err) {
      console.error("Error occurred while fetching cities", { err });
      res.status(500).json({
        message: "Error occurred while fetching cities"
      });
    }
  };
}

// routes/CityRouter.ts
import express from 'express';
import { CityService } from '../services/CityService';
import { CityController } from '../controllers/CityController';

const router = express.Router();

const cityService = new CityService();
const cityController = new CityController(cityService);

router.get('/cities', cityController.getCities);

export default router;

`;

function createDependencyInversionPrincipleData() {
  return { 
    titleOne: 'Without Dependency Inversion Principle',
    TitleTwo: 'With Dependency Inversion Principle',
    snippetOne: codeWithoutDependencyInversionPrinciple,
    snippetTwo: codeWithDependencyInversionPrinciple,
  };
}


export default function SolidPrinciplesBlog06() {
    return (
        <Container id="SolidPrinciplesBlog" sx={{ 
          my: { 
            sm: 0, 
            xs: 5 
          }}} 
        fixed>
          <Box sx={{  
            minHeight: '50vh',
          }}>
              <Typography variant="h1" component="h1" align="center">
                Solid Principles in Javascript
              </Typography>
              <BlogPublishInfo date="4th June 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '4vw'} }}>
                <img
                    srcSet={compoundComponentsPng}
                    alt={"solidPrinciples"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                <b>S.O.L.I.D</b> is a set of object-oriented design principles which are widely used in the software industry and has proven track record of helping with development of scalable applications.
                this principles can be applied more efficiently in Javascript via Typescript
                <br />
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What are S.O.L.I.D Principles?
              </Typography>
              <Typography variant="body1" align="left">
              <b>S.O.L.I.D</b> stands for:

              <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                <ListItem sx={{ display: 'list-item' }}>
                  <b>S</b> - Single Responsibility Principle
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                  <b>O</b> - Open-Closed Principle
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                  <b>L</b> - Liskov Segregation Principle
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                  <b>I</b> - Interface Segregation Principle
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                  <b>D</b> - Dependency Inversion Principle
                </ListItem>
              </List>
              </Typography>
                <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why S.O.L.I.D Principles?
              </Typography>
              <Typography variant="body1" align="left">
                The concepts help us write clean, highly maintainable, highly readable and scalable code.
                <br/>
                Helps us during debugging to pinpoint issues more easily
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Principles in action
              </Typography>
              <Typography variant="body1" align="left">
                Let us now see examples of the principles in action using Typescript demonstrate the enforcement of the SOLID principles more clearly
              </Typography>
          </Box>
          <br/>
          <Container id="single_responsibility_principle">
            <Box>
              <Typography variant="h4" align="left">
                  Single responsibility principle
                </Typography>
                <br/>
                <Typography variant="body1" align="left">
                  According to this principle, a class, a module or a function should have only one reason to change, that is, it should have only one task or responsibility.
                </Typography>
            </Box>
            <Box sx={{
              minWidth: {
                sm: '80vw', 
                xs: '100vw',
              },
              marginLeft: {
                sm: '-9vw', 
                xs: '-10vw',
              },
            }}>
                <br/><br/>
                <PrincipleWrapper data={createSingleResponsibilityData()} />
                <br/><br/>
            </Box>
          </Container>
          <Container id="open_closed_principle">
            <Box>
            <Typography variant="h4" align="left">
                Open-Closed principle
              </Typography>
              <br/>
              <Typography variant="body1" align="left">
                Software entities(classes, modules, functions, etc.) should be open for extension, but closed for modification.
                <br/>
                The aim is that we should be able to add new features to applications easily and the changes should be non-breaking and should not require refactoring existing functionality
                <br/>
                We can use abstraction and have the new functionality just implement the abstraction thus avoiding changes in the functionality itself
              </Typography>
            </Box>
            <Box sx={{
              minWidth: {
                sm: '80vw', 
                xs: '100vw',
              },
              marginLeft: {
                sm: '-9vw', 
                xs: '-10vw',
              },
            }}>
                <br/><br/>
                <PrincipleWrapper data={createOpenClosedPrincipleData()} />
                <br/><br/>
            </Box>
          </Container>
          <Container id="liskov_substitution_principle">
            <Box>
              <Typography variant="h4" align="left">
                Liskov Substitution principle
              </Typography>
              <br/>
              <Typography variant="body1" align="left">
                In the previous section we saw that using abstraction we adhered to the rule but for this principle Abstractions alone are not enough
                <br /> <br />
                Objects of a superclass should be replaceable by objects of a subclass without affecting the correctness of the program.
                <br/>
                Definition: If S is a subtype of T, then objects of type T may be replaced with objects of type S without altering the correctness of the program.
                <br/>
                basically we should not replace a parent class with its subclasses if they create unexpected behaviours in the application
                <br /><br />
               
              </Typography>
            </Box>
            <Box sx={{
              minWidth: {
                sm: '80vw', 
                xs: '100vw',
              },
              marginLeft: {
                sm: '-9vw', 
                xs: '-10vw',
              },
            }}>
                <br/><br/>
                <PrincipleWrapper data={createLiskovSubstitutionPrincipleData()} />
                <br/><br/>
            </Box>
            In the above example with liskov substitution principle applied
                <br />
                Whoever uses Resource expects:
                <br />
                  <b>getAvailableRoles()</b> → a valid array of roles, including metadata like attendance_multiplier, is_primary, etc.
                  <br />
                  <b>getExpandedRoles()</b> → roles matched with attendance records
                  <br />
                  <b>getBasicInfo()</b> → basic ID and profile information
                  <br />
                  Now both LabourerResource and ContractorResource implement this behavior without breaking any of those expectations.
                  <br />
                  
                  This means the consumer of the Resource interface doesn't need to know or care whether it’s a Labourer or Contractor.
                  <br />
                  <br />
                  The object it receives behaves correctly, as expected — and that is the essence of LSP.
                  <br />
                  <br />
          </Container>
          <Container id="interface_segregation_principle">
            <Box>
              <Typography variant="h4" align="left">
                Interface Segregation principle
              </Typography>
              <br/>
              <Typography variant="body1" align="left">
                No class should be forced to implement interfaces or methods that it will not use.

                <br />
                Interfaces should be small and focused.
                <br />
                A class should only implement what it actually needs.
                <br />
                Avoid “fat” interfaces that lump unrelated responsibilities together.

                <br/> <br/>
                Now let us go a step further with the interface we created in the previous example, we dont want to pollute common interfaces with methods not all clients need.

              </Typography>
            </Box>
            <Box sx={{
              minWidth: {
                sm: '80vw', 
                xs: '100vw',
              },
              marginLeft: {
                sm: '-9vw', 
                xs: '-10vw',
              },
            }}>
                <br/><br/>
                <PrincipleWrapper data={createInterfaceSegregationPrincipleData()} />
                <br/><br/>
            </Box>
          </Container>
          <Container id="dependency_inversion_principle">
            <Box>
              <Typography variant="h4" align="left">
                Dependency Inversion principle
              </Typography>
              <br/>
              <Typography variant="body1" align="left">
                High-level modules, i.e., the modules that contain strategic decisions and high-level directives, should not depend on low-level modules, which are the modules that contain detailed and low-level logic.
                Both, high-level and low-level modules, should depend on abstractions.

                <br/><br/>
                
                In the below example we highlight that High-level modules (e.g., controllers) should not depend on low-level modules (e.g., services or DB logic).
                Both should depend on abstractions (e.g., interfaces).
              </Typography>
            </Box>
            <Box sx={{
              minWidth: {
                sm: '80vw', 
                xs: '100vw',
              },
              marginLeft: {
                sm: '-9vw', 
                xs: '-10vw',
              },
            }}>
                <br/><br/>
                <PrincipleWrapper data={createDependencyInversionPrincipleData()} />
                <br/><br/>
            </Box>
          </Container>
        </Container>
    )
};